import shiPalette from "../../theme/shiPalette";

interface OrderStatusSource {
  fulfillmentStatus?: string | number | null;
  status?: string | null;
  fulfillmentGroups?: unknown;
}

export type ItProjectOrderStatusIconName =
  | "HowToReg"
  | "HourglassEmptyOutlined"
  | "ShoppingCartOutlined"
  | "ApartmentOutlined"
  | "LocalShipping"
  | "Inventory"
  | "ReplayCircleFilled"
  | "BlockOutlined"
  | "CancelOutlined";

interface ItProjectOrderStatusChipMetadata {
  borderColor: string;
  textColor?: string;
  iconColor?: string;
  iconName: ItProjectOrderStatusIconName;
}

const STATUS_LABELS: Record<string, string> = {
  AwaitingApproval: "Awaiting Approval",
  OrderPlaced: "Order Placed",
  Processing: "Processing",
  PartiallyShipped: "Partially Shipped",
  Shipped: "Shipped",
  Delivered: "Delivered",
  TransferApprover: "Transfer Approver",
  Denied: "Denied",
  ApprovalExpired: "Approval Expired",
  PoRequested: "PO Requested",
  PendingReturn: "Pending Return",
  Returned: "Returned",
  Cancelled: "Cancelled",
  NotApplicable: "Not Applicable",
};

const DEFAULT_STATUS_CHIP_METADATA: ItProjectOrderStatusChipMetadata = {
  borderColor: "text.secondary",
  iconName: "HourglassEmptyOutlined",
};

// Visual language (color + icon per status) mirrors the Orders page status
// column, which is the source of truth: getOrderStatusChip in
// customer-experience/.../orders-features/components/orderStatusChips.tsx.
// shared-ui cannot import that module, so keep these values in sync by hand.
const STATUS_CHIP_METADATA: Record<string, ItProjectOrderStatusChipMetadata> = {
  AwaitingApproval: {
    borderColor: "error.light",
    iconColor: "error.light",
    iconName: "HowToReg",
  },
  OrderPlaced: {
    borderColor: "inherit.main",
    iconName: "ShoppingCartOutlined",
  },
  Processing: {
    borderColor: "primary.main",
    iconName: "Inventory",
  },
  PartiallyShipped: {
    borderColor: "secondary.main",
    iconName: "LocalShipping",
  },
  Shipped: {
    borderColor: "secondary.main",
    iconName: "LocalShipping",
  },
  Delivered: {
    borderColor: shiPalette.success["50p"],
    iconColor: shiPalette.success.light,
    iconName: "ApartmentOutlined",
  },
  Denied: {
    borderColor: "error.light",
    textColor: "text.primary",
    iconColor: "error.light",
    iconName: "BlockOutlined",
  },
  Returned: {
    borderColor: "text.inherit",
    iconName: "ReplayCircleFilled",
  },
  Cancelled: {
    borderColor: "text.inherit",
    iconName: "CancelOutlined",
  },
};

const FULFILLMENT_STATUS_BY_ENUM_VALUE: Record<number, string> = {
  0: "AwaitingApproval",
  1: "TransferApprover",
  2: "Denied",
  3: "ApprovalExpired",
  4: "PoRequested",
  5: "OrderPlaced",
  6: "Processing",
  7: "PartiallyShipped",
  8: "Shipped",
  9: "Delivered",
  10: "PendingReturn",
  11: "Returned",
  12: "Cancelled",
  13: "NotApplicable",
};

function normalizeStatusKey(value: string) {
  return value.replace(/\s+/g, "").toLowerCase();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function toCanonicalStatus(value: string) {
  switch (normalizeStatusKey(value)) {
    case "awaitingapproval":
      return "AwaitingApproval";
    case "orderplaced":
      return "OrderPlaced";
    case "openorder":
    case "processing":
      return "Processing";
    case "partiallyshipped":
      return "PartiallyShipped";
    case "intransit":
    case "shipped":
      return "Shipped";
    case "delivered":
      return "Delivered";
    case "transferapprover":
      return "TransferApprover";
    case "rd":
    case "denied":
      return "Denied";
    case "e":
    case "approvalexpired":
      return "ApprovalExpired";
    case "porequested":
      return "PoRequested";
    case "pendingreturn":
      return "PendingReturn";
    case "returned":
      return "Returned";
    case "cancelled":
    case "canceled":
      return "Cancelled";
    case "notapplicable":
      return "NotApplicable";
    default:
      return value.trim();
  }
}

function isCanonicalFulfillmentStatus(value: string) {
  const canonical = toCanonicalStatus(value);

  return canonical in STATUS_LABELS;
}

function getFulfillmentGroupStatusKeys(
  fulfillmentGroups?: unknown,
): string[] {
  if (!isRecord(fulfillmentGroups)) {
    return [];
  }

  const groups = isRecord(fulfillmentGroups.additionalData)
    ? {
        ...fulfillmentGroups,
        ...fulfillmentGroups.additionalData,
      }
    : fulfillmentGroups;

  return Object.keys(groups)
    .filter((key) => key !== "additionalData")
    .map((key) => key.replace(/[()]/g, "").split(",")[0]?.trim() ?? "")
    .filter((key) => key.length > 0)
    .map(toCanonicalStatus)
    .filter((key) => key in STATUS_LABELS);
}

function resolveStatusFromFulfillmentGroups(
  fulfillmentGroups?: unknown,
): string | undefined {
  const groupStatuses = getFulfillmentGroupStatusKeys(fulfillmentGroups);

  if (groupStatuses.length === 0) {
    return undefined;
  }

  const activeStatuses = groupStatuses.filter(
    (status) => status !== "NotApplicable",
  );

  if (activeStatuses.length === 0) {
    return "Processing";
  }

  if (activeStatuses.every((status) => status === activeStatuses[0])) {
    return activeStatuses[0];
  }

  const activeStatusSet = new Set(activeStatuses);
  const hasProcessingLikeStatus = [
    "AwaitingApproval",
    "OrderPlaced",
    "Processing",
  ].some((status) => activeStatusSet.has(status));
  const hasShippedLikeStatus = ["PartiallyShipped", "Shipped", "Delivered"].some(
    (status) => activeStatusSet.has(status),
  );

  if (activeStatusSet.has("PartiallyShipped")) {
    return "PartiallyShipped";
  }

  if (hasProcessingLikeStatus && hasShippedLikeStatus) {
    return "PartiallyShipped";
  }

  if (activeStatusSet.has("Shipped") && activeStatusSet.has("Delivered")) {
    return "PartiallyShipped";
  }

  if (activeStatusSet.has("Delivered")) {
    return "Delivered";
  }

  if (activeStatusSet.has("Shipped")) {
    return "Shipped";
  }

  if (hasProcessingLikeStatus) {
    return "Processing";
  }

  return activeStatuses[0];
}

function titleCaseStatus(value: string) {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function resolveItProjectOrderStatusValue({
  fulfillmentStatus,
  status,
  fulfillmentGroups,
}: Readonly<OrderStatusSource>) {
  const rawStatus = typeof status === "string" ? toCanonicalStatus(status) : undefined;
  if (rawStatus === "Denied" || rawStatus === "ApprovalExpired") {
    return rawStatus;
  }

  if (typeof fulfillmentStatus === "number") {
    return FULFILLMENT_STATUS_BY_ENUM_VALUE[fulfillmentStatus];
  }

  if (
    typeof fulfillmentStatus === "string" &&
    fulfillmentStatus.trim() &&
    isCanonicalFulfillmentStatus(fulfillmentStatus)
  ) {
    return toCanonicalStatus(fulfillmentStatus);
  }

  const fulfillmentGroupStatus = resolveStatusFromFulfillmentGroups(
    fulfillmentGroups,
  );

  if (fulfillmentGroupStatus) {
    return fulfillmentGroupStatus;
  }

  if (rawStatus && isCanonicalFulfillmentStatus(rawStatus)) {
    return rawStatus;
  }

  return undefined;
}

export function formatItProjectOrderStatusLabel(status?: string | null) {
  if (!status?.trim()) {
    return "-";
  }

  return STATUS_LABELS[status] ?? titleCaseStatus(status.trim());
}

export function getItProjectOrderStatusChipMetadata(
  status?: string | null,
): ItProjectOrderStatusChipMetadata {
  if (!status?.trim()) {
    return DEFAULT_STATUS_CHIP_METADATA;
  }

  return STATUS_CHIP_METADATA[status] ?? DEFAULT_STATUS_CHIP_METADATA;
}